import { z } from 'zod';
/**
 * 자료실(lib) 공용 값 규칙 — 서버 입력 스키마(apps/api dto/lib.schema)와 운영자·사용자
 * 화면(apps/admin·apps/web)이 같은 상한·형식·파서를 보게 하는 단일 출처.
 *
 * 여기 두는 기준: zod 외 의존이 없는 순수 값이고, 두 앱 이상이 같은 값을 써야 한다.
 * 서버 전용 규칙(다운로드 파일명 sanitize, 객체 키 prefix)은 여기 오지 않는다.
 * 상한값은 apps/api/prisma/schema.prisma 의 컬럼 정의와 1:1 이다 (D9).
 */
// ---- 상한 ----
/**
 * 자료 파일 한 개 상한. 업로드는 브라우저가 스토리지로 바로 올려 서버를 거치지 않지만,
 * 운영자 브라우저가 파일 전체를 읽어 검사값(SHA-256)을 만들고 받을 때마다 전송 요금이 나간다.
 */
export const ASSET_FILE_MAX_BYTES = 50 * 1024 * 1024;
export const SCREENSHOT_MAX_BYTES = 5 * 1024 * 1024;
export const VIDEO_TITLE_MAX = 200;
export const VIDEO_SUMMARY_MAX = 300;
export const VIDEO_SLUG_MAX = 120;
export const CHAPTER_TITLE_MAX = 30;
export const CHAPTER_BODY_MAX = 150;
export const ASSET_TITLE_MAX = 200;
export const ASSET_KIND_LABEL_MAX = 30;
export const FILENAME_MAX = 255;
/** 하루(86400초) 미만 — 유튜브 타임스탬프가 이 범위를 벗어나면 입력 실수다 */
export const TIME_SEC_MAX = 86_399;
/** presign 일괄 발급 상한 — 한 영상에 이보다 많은 자료 파일은 등록 실수에 가깝다 */
export const UPLOAD_BATCH_MAX = 20;
// ---- 형식 ----
export const YOUTUBE_ID_PATTERN = /^[A-Za-z0-9_-]{11}$/;
export const SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
export const youtubeIdSchema = z
    .string()
    .regex(YOUTUBE_ID_PATTERN, '유튜브 영상 ID 는 11자여야 합니다');
export const slugSchema = z
    .string()
    .min(1)
    .max(VIDEO_SLUG_MAX)
    .regex(SLUG_PATTERN, 'slug 는 소문자·숫자·하이픈만 쓸 수 있습니다');
export const timeSecSchema = z.number().int().min(0).max(TIME_SEC_MAX);
export const SCREENSHOT_MIME_TYPES = ['image/png', 'image/jpeg', 'image/webp'];
export const screenshotMimeSchema = z.enum(SCREENSHOT_MIME_TYPES);
/** 허용 MIME ↔ 객체 키 확장자. MIME 을 추가하면 이 표가 컴파일에서 완전성을 요구한다. */
export const SCREENSHOT_MIME_TO_EXT = {
    'image/png': 'png',
    'image/jpeg': 'jpg',
    'image/webp': 'webp',
};
export const screenshotExtensionOf = (mimeType) => SCREENSHOT_MIME_TO_EXT[mimeType];
/** 객체 키 확장자 → 응답 Content-Type. 허용 목록 밖이면 null — 호출자가 거부한다. */
export const screenshotMimeOfExtension = (extension) => {
    for (const mimeType of SCREENSHOT_MIME_TYPES) {
        if (SCREENSHOT_MIME_TO_EXT[mimeType] === extension) {
            return mimeType;
        }
    }
    return null;
};
/** 자료 종류의 화면 표기 — 운영자 배치 보드·사용자 카드가 같은 이름을 쓴다. */
export const ASSET_FORMAT_LABEL = {
    PDF: 'PDF',
    PPT: 'PPT',
    EXCEL: '엑셀',
    WORD: '워드',
    HWP: '한글',
    ZIP: 'ZIP',
    IMAGE: '이미지',
    LINK: '링크',
};
const BYTES_PER_KB = 1_024;
const BYTES_PER_MB = BYTES_PER_KB * BYTES_PER_KB;
/** 바이트 → "2.2 MB" / "500 KB" / "512 B" — 운영자 화면과 사용자 카드가 같은 표기를 쓴다. */
export const formatBytes = (bytes) => {
    if (bytes >= BYTES_PER_MB) {
        return `${(bytes / BYTES_PER_MB).toFixed(1)} MB`;
    }
    if (bytes >= BYTES_PER_KB) {
        return `${Math.round(bytes / BYTES_PER_KB)} KB`;
    }
    return `${bytes} B`;
};
/** 자료 부제목 — 종류 라벨 · 형식 · 용량 (D5). 링크 자료는 용량이 없어 빠진다. */
export const assetMetaOf = (asset) => [
    asset.kindLabel,
    ASSET_FORMAT_LABEL[asset.format],
    ...(asset.sizeBytes === null ? [] : [formatBytes(asset.sizeBytes)]),
].join(' · ');
/**
 * 허용 확장자 → 파일 종류(MIME)·자료 종류. 올릴 수 있는 형식은 이 표가 전부다.
 *
 * MIME 은 브라우저가 알려 주는 값이 아니라 이 표에서 정한다 — .hwp 처럼 브라우저가 종류를
 * 비워 보내는 형식이 있다. 업로드 서명과 다운로드 Content-Type 이 이 값을 쓰고, 다운로드는
 * 늘 저장(attachment)이라 확장자만 믿어도 브라우저가 내용을 실행하지 않는다.
 * SVG 는 뺐다 — 그림 파일 안에 스크립트를 넣을 수 있다.
 */
export const ASSET_FILE_TYPES = {
    pdf: { mimeType: 'application/pdf', format: 'PDF' },
    ppt: { mimeType: 'application/vnd.ms-powerpoint', format: 'PPT' },
    pptx: {
        mimeType: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        format: 'PPT',
    },
    xls: { mimeType: 'application/vnd.ms-excel', format: 'EXCEL' },
    xlsx: {
        mimeType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        format: 'EXCEL',
    },
    doc: { mimeType: 'application/msword', format: 'WORD' },
    docx: {
        mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        format: 'WORD',
    },
    hwp: { mimeType: 'application/x-hwp', format: 'HWP' },
    hwpx: { mimeType: 'application/vnd.hancom.hwpx', format: 'HWP' },
    zip: { mimeType: 'application/zip', format: 'ZIP' },
    png: { mimeType: 'image/png', format: 'IMAGE' },
    jpg: { mimeType: 'image/jpeg', format: 'IMAGE' },
    jpeg: { mimeType: 'image/jpeg', format: 'IMAGE' },
    webp: { mimeType: 'image/webp', format: 'IMAGE' },
    gif: { mimeType: 'image/gif', format: 'IMAGE' },
};
export const ASSET_FILE_EXTENSIONS = Object.keys(ASSET_FILE_TYPES);
/** 파일 선택창 accept 값 — 허용 형식만 고르게 한다. */
export const ASSET_FILE_ACCEPT = ASSET_FILE_EXTENSIONS.map((extension) => `.${extension}`).join(',');
const isAssetFileExtension = (value) => Object.hasOwn(ASSET_FILE_TYPES, value);
/** 파일명의 마지막 확장자로 형식을 찾는다. 허용 목록 밖이면 null — 호출자가 거부한다. */
export const assetFileTypeOf = (filename) => {
    const dot = filename.lastIndexOf('.');
    if (dot < 0) {
        return null;
    }
    const extension = filename.slice(dot + 1).toLowerCase();
    if (!isAssetFileExtension(extension)) {
        return null;
    }
    const { mimeType, format } = ASSET_FILE_TYPES[extension];
    return { extension, mimeType, format };
};
// ---- 링크 자료 ----
/** 링크 자료 주소 상한 — schema.prisma link_url VARCHAR(2048). 인터넷 주소 길이의 통상 상한이다. */
export const ASSET_LINK_URL_MAX = 2_048;
const HTTPS_PREFIX = 'https://';
/** 소문자 https:// 로 시작하고 주소로 읽히는가 — DB 제약(link_url LIKE 'https://%')과 같은 기준. */
const isHttpsUrl = (value) => {
    if (!value.startsWith(HTTPS_PREFIX)) {
        return false;
    }
    try {
        return new URL(value).hostname.length > 0;
    }
    catch {
        return false;
    }
};
/**
 * 링크 자료 주소. https 만 받는다 — 이 주소로 사용자를 보내므로 javascript:·data: 같은
 * 스크립트 주소가 끼면 우리 사이트 이름으로 남의 코드를 돌리게 된다.
 */
export const assetLinkUrlSchema = z
    .string()
    .trim()
    .max(ASSET_LINK_URL_MAX)
    .refine(isHttpsUrl, 'https:// 로 시작하는 주소만 넣을 수 있습니다');
// ---- 유튜브 ----
const YOUTUBE_URL_PATTERNS = [
    /(?:youtube\.com|youtube-nocookie\.com)\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/|live\/)([A-Za-z0-9_-]{11})/,
    /youtu\.be\/([A-Za-z0-9_-]{11})/,
];
/**
 * 유튜브 URL 또는 11자 ID → 11자 ID. 실패 시 null (LIB-A-01 형식 검증의 근거).
 * URL 원문은 저장하지 않는다 — schema.prisma 의 youtube_id 주석 참조.
 */
export const extractYoutubeId = (input) => {
    const trimmed = input.trim();
    if (YOUTUBE_ID_PATTERN.test(trimmed)) {
        return trimmed;
    }
    for (const pattern of YOUTUBE_URL_PATTERNS) {
        const match = trimmed.match(pattern);
        if (match?.[1] !== undefined) {
            return match[1];
        }
    }
    return null;
};
/** 유튜브가 제공하는 표준 썸네일 — 목록 카드와 등록 폼 미리보기가 같은 소스를 쓴다. */
export const youtubeThumbnailUrl = (youtubeId) => `https://i.ytimg.com/vi/${youtubeId}/hqdefault.jpg`;
// ---- 타임코드 ----
const SECONDS_PER_HOUR = 3_600;
const SECONDS_PER_MINUTE = 60;
const TIMECODE_PATTERN = /^(?:(\d{1,2}):)?([0-5]?\d):([0-5]\d)$/;
/** "mm:ss" 또는 "h:mm:ss" → 초. 형식이 어긋나면 null (챕터 편집기 입력 검증). */
export const parseTimecode = (input) => {
    const match = input.trim().match(TIMECODE_PATTERN);
    if (!match) {
        return null;
    }
    const hours = match[1] !== undefined ? Number(match[1]) : 0;
    const minutes = Number(match[2]);
    const seconds = Number(match[3]);
    return hours * SECONDS_PER_HOUR + minutes * SECONDS_PER_MINUTE + seconds;
};
const pad = (value) => String(value).padStart(2, '0');
/** 초 → "mm:ss" 또는 "h:mm:ss" — 챕터 시점 표기와 t= 딥링크 라벨의 유일한 포맷. */
export const formatTimeSec = (timeSec) => {
    const safe = Math.max(0, Math.trunc(timeSec));
    const hours = Math.floor(safe / SECONDS_PER_HOUR);
    const minutes = Math.floor((safe % SECONDS_PER_HOUR) / SECONDS_PER_MINUTE);
    const seconds = safe % SECONDS_PER_MINUTE;
    const mmss = `${pad(minutes)}:${pad(seconds)}`;
    return hours > 0 ? `${hours}:${mmss}` : mmss;
};
